import axios from 'axios';
import axiosRetry from 'axios-retry';
import { EventSource } from 'eventsource';
/**
 *  !IMPORTANT this script is used in a command line script
 *  Therefore, DO NOT use console.log() statements unless it is wrapped in a condition checking the logToConsole property.
 *  Otherwise, you will break CLI usage that the test runner depends on
 **/
class Openai {
    threadId = null;
    assistantId = null;
    runId = null;
    baseUrl = '';
    type = null;
    recordId = null;
    pageId = null;
    instructions = '';
    instructionsRequest = null;
    runStatus = null;
    feedback = '';
    grade = null;
    aiScoreFailed = false;
    completedRunIds = [];
    scoringPollTimer = null;
    feedbackPollTimer = null;
    aborted = false;
    abortController = new AbortController();
    functionCallsHandled = false;
    eventSource = null;
    apiToken = '';
    outputFormat = 'text';
    logToConsole = false;
    // Responses API support (all models use this now)
    conversationHistory = [];
    threadDbId = null;
    // Callback properties (set via setMessagesCallback and setScoreCallback)
    getMessagesCallback = null;
    getScoreCallback = null;
    constructor(type = null, assistantId = null, recordId = null, pageId = null) {
        // @ts-ignore
        this.logToConsole = window.debugOpenAI ?? false;
        this.type = type;
        this.recordId = recordId;
        this.pageId = pageId;
        this.assistantId = assistantId;
        if (typeof window !== 'undefined') {
            this.baseUrl = '//' + window.location.hostname;
            // @ts-ignore
            this.apiToken = window.apiToken;
            axios.defaults.headers.common['x-api-token'] = this.apiToken;
        }
    }
    /**
     * Initialize (kept for backward compatibility but no longer needed)
     * All assistants now use Responses API, no configuration loading required
     */
    async initialize() {
        // No-op: All models now use Responses API
        return this;
    }
    abortOperations() {
        this.aborted = true;
        this.killFeedbackPollTimer();
        this.killScoringPollTimer();
        this.abortController.abort();
        this.abortController = new AbortController(); // Reset for future operations
    }
    stripHtml(html) {
        try {
            return html.replace(/<[^>]*>?/g, '');
        }
        catch (e) {
            return html;
        }
    }
    setBaseUrl(url) {
        this.baseUrl = url;
    }
    setAPIToken(token) {
        this.apiToken = token;
        axios.defaults.headers.common['x-api-token'] = this.apiToken;
    }
    setInstructions = async (instructions, replacementTokens = null, promptKey = null) => {
        this.instructions = instructions;
        // Make API call to update instructions in database
        // @ts-ignore
        axiosRetry(axios, {
            retries: 3,
            retryCondition: () => true,
            retryDelay: (retryCount) => {
                return retryCount * 1000;
            }
        });
        try {
            this.instructionsRequest = axios.post(`${this.baseUrl}/api/openai/thread/${this.threadId}/instructions`, {
                instructions: instructions,
                replacement_tokens: replacementTokens,
                prompt_key: promptKey
            });
            await this.instructionsRequest;
        }
        catch (error) {
            console.error('Error updating thread instructions:', error);
        }
    };
    setMessagesCallback(callback) {
        this.getMessagesCallback = callback;
    }
    setScoreCallback(callback) {
        this.getScoreCallback = callback;
    }
    getScoringAssistants = async () => {
        // @ts-ignore
        axiosRetry(axios, {
            retries: 3, retryCondition: () => true, retryDelay: (retryCount) => {
                return retryCount * 1000;
            }
        });
        let resp = await axios.get(`//${window.location.hostname}/api/openai/assistant/?type=scoring`);
        return resp.data;
    };
    getScoringAssistantById = async (id) => {
        // @ts-ignore
        axiosRetry(axios, {
            retries: 3, retryCondition: () => true, retryDelay: (retryCount) => {
                return retryCount * 1000;
            }
        });
        let resp = await axios.get(`//${window.location.hostname}/api/openai/assistant/${id}`);
        return resp.data;
    };
    getAssistantsByType = async (type) => {
        // @ts-ignore
        axiosRetry(axios, {
            retries: 3, retryCondition: () => true, retryDelay: (retryCount) => {
                return retryCount * 1000;
            }
        });
        let resp = await axios.get(`//${window.location.hostname}/api/openai/assistant/?type=${type}`);
        return resp.data;
    };
    extractTextFromResponse = (text) => {
        if (this.outputFormat === 'json_schema') {
            try {
                let json = JSON.parse(text);
                if (this.logToConsole) {
                    console.log(json);
                }
                return json.text ?? 'No text property is configured in json_schema';
            }
            catch (error) {
                // Log error for debugging but don't break - return text as-is
                if (this.logToConsole) {
                    console.error('Failed to parse JSON schema response:', error);
                    console.log('Raw text:', text.substring(0, 200) + (text.length > 200 ? '...' : ''));
                }
                // If JSON parsing fails, return the text as-is (might be incomplete/streaming)
                return text;
            }
        }
        else if (this.outputFormat === 'text') {
            // eventually all assistants should be converted to json_schema
            // this is also to handle backwards compatability for polling
            try {
                let json = JSON.parse(text);
                if (this.logToConsole) {
                    console.log(json);
                }
                return json.text ?? text;
            }
            catch (error) {
                if (this.logToConsole) {
                    console.log(text);
                }
                return text;
            }
        }
        else {
            return text;
        }
    };
    async createThread() {
        try {
            if (this.logToConsole) {
                console.log('=== createThread ===');
                console.log('Type:', this.type);
                console.log('Assistant ID:', this.assistantId);
                console.log('Record ID:', this.recordId);
                console.log('Page ID:', this.pageId);
            }
            // @ts-ignore
            axiosRetry(axios, {
                retries: 3, retryCondition: () => true, retryDelay: (retryCount) => {
                    return retryCount * 1000;
                }
            });
            let resp = await axios.post(`${this.baseUrl}/api/openai/thread`, {
                type: this.type,
                record_id: this.recordId,
                page_id: this.pageId,
                assistant_id: this.assistantId // Include assistant ID for thread tracking
            });
            if (this.logToConsole) {
                console.log('Thread created response:', resp.data);
            }
            // All models now use local database threads
            this.threadDbId = resp.data.db_id || resp.data.id;
            this.threadId = resp.data.thread_id || this.threadDbId; // Backward compatibility
            this.conversationHistory = []; // Initialize local conversation
            this.functionCallsHandled = false; // Reset for new thread (important for retries)
            return true;
        }
        catch (e) {
            if (this.logToConsole) {
                console.error(e);
            }
            return false;
        }
    }
    async addMessageToThread(content, runAssistant = true, role = 'user') {
        try {
            const safeContent = content ?? '';
            // Add to local conversation history
            this.conversationHistory.push({
                role: role,
                content: this.stripHtml(safeContent)
            });
            if (this.logToConsole) {
                console.log('=== addMessageToThread ===');
                console.log('Role:', role);
                console.log('Content length:', this.stripHtml(safeContent)?.length ?? 'N/A');
                console.log('Thread DB ID:', this.threadDbId);
                console.log('Local conversation history length:', this.conversationHistory.length);
            }
            const strippedContent = this.stripHtml(safeContent);
            // Always persist all messages to database
            // Backend will load conversation from DB (no URL length limits)
            if (this.logToConsole) {
                console.log('Persisting to DB');
            }
            if (!this.threadDbId) {
                if (this.logToConsole) {
                    console.error('Cannot persist message: threadDbId is null');
                }
                return;
            }
            // @ts-ignore
            axiosRetry(axios, {
                retries: 3,
                retryCondition: () => true,
                retryDelay: (retryCount) => {
                    return retryCount * 1000;
                }
            });
            const response = await axios.post(`${this.baseUrl}/api/openai/thread/${this.threadDbId}/message`, {
                role: role,
                content: strippedContent ?? ''
            });
            if (this.logToConsole) {
                console.log('✅ Message persisted to DB:', response.data);
            }
            if (runAssistant) {
                await this.runAssistant();
            }
        }
        catch (e) {
            if (this.logToConsole) {
                if (e.name === 'AbortError') {
                    console.error('addMessageToThread was aborted');
                }
                else {
                    console.error('Error in addMessageToThread:', e);
                }
            }
        }
    }
    /**
     * Run assistant (all models use streaming now)
     */
    async runAssistant() {
        await this.runAssistantModern();
    }
    /**
     * LEGACY: Assistants API implementation (for GPT-4o, GPT-4, GPT-3.5)
     * Uses thread/run/poll pattern
     */
    async runAssistantLegacy() {
        try {
            // @ts-ignore
            axiosRetry(axios, {
                retries: 3, retryCondition: () => true, retryDelay: (retryCount) => {
                    return retryCount * 1000;
                }
            });
            // There can only ever be one run in progress at a time so let's check for that and use it, so we don't create a new run and error out.
            // This can happen if a prior call to runAssistant failed or timed out
            let runs = await axios.get(`${this.baseUrl}/api/openai/thread/${this.threadId}/runs`, {
                signal: this.abortController.signal
            });
            if (runs.data.data.length > 0) {
                // filter runs where status is in_progress
                let inProgress = runs.data.data.filter((run) => {
                    return run.status === 'in_progress';
                });
                // if there are in_progress runs we want to use the first one
                if (inProgress.length > 0) {
                    this.runId = inProgress[0].id;
                    if (this.type === 'feedback' || this.type === 'meta') {
                        this.pollStatus();
                    }
                    else if (this.type === 'scoring') {
                        this.pollScoringStatus();
                    }
                    return;
                }
            }
            // Create a new run if we didn't find an existing one
            let data = {
                assistant_id: this.assistantId,
                additional_instructions: this.instructions
            };
            if (this.type === 'scoring') {
                data.additional_instructions = 'Please score the following response using the evaluation rubric provided in the assistants instructions.';
                data.force_function_calls = true;
            }
            let resp = await axios.post(`${this.baseUrl}/api/openai/thread/${this.threadId}/run`, data);
            // if resp.data.id is undefined throw an error
            if (!resp.data.id) {
                throw new Error('Run ID is undefined');
            }
            this.runId = resp.data.id;
            if (this.type === 'feedback' || this.type === 'meta') {
                this.pollStatus();
            }
            else if (this.type === 'scoring') {
                this.pollScoringStatus();
            }
        }
        catch (e) {
            if (this.logToConsole) {
                if (e.name === 'AbortError') {
                    console.error('runAssistantLegacy was aborted');
                }
                else {
                    console.error(e);
                }
            }
        }
    }
    /**
     * MODERN: Responses API implementation (for GPT-5+)
     * Direct response - no polling needed!
     */
    async runAssistantModern() {
        try {
            // @ts-ignore
            axiosRetry(axios, {
                retries: 3, retryCondition: () => true, retryDelay: (retryCount) => {
                    return retryCount * 1000;
                }
            });
            // Get the last user message from local conversation
            const lastMessage = this.conversationHistory[this.conversationHistory.length - 1];
            if (!lastMessage) {
                throw new Error('No message in conversation history');
            }
            // Direct API call to Responses API - NO POLLING!
            let resp = await axios.post(`${this.baseUrl}/api/openai/response`, {
                assistant_id: this.assistantId,
                thread_db_id: this.threadDbId,
                content: lastMessage.content,
                additional_instructions: this.instructions,
                signal: this.abortController.signal
            });
            const responseData = resp.data;
            // Check for errors
            if (responseData.error) {
                throw new Error(responseData.error);
            }
            // Handle tool calls for scoring (Responses API format)
            if (responseData.tool_calls) {
                this.handleToolCalls(responseData.tool_calls);
            }
            else {
                // Regular response
                const assistantMessage = responseData.response || '';
                // Add to local conversation
                this.conversationHistory.push({
                    role: 'role',
                    content: assistantMessage
                });
                this.feedback = this.extractTextFromResponse(assistantMessage);
                // Call messages callback immediately (no polling needed!)
                if (this.getMessagesCallback) {
                    const messages = this.conversationHistory.map(msg => ({
                        role: msg.role,
                        text: this.extractTextFromResponse(msg.content)
                    }));
                    this.getMessagesCallback(this, messages);
                }
            }
        }
        catch (e) {
            if (this.logToConsole) {
                if (e.name === 'AbortError') {
                    console.error('runAssistantModern was aborted');
                }
                else {
                    console.error(e);
                }
            }
        }
    }
    async pollStatus() {
        clearInterval(this.feedbackPollTimer);
        this.feedbackPollTimer = setInterval(async () => {
            try {
                let response = await axios.get(`${this.baseUrl}/api/openai/thread/${this.threadId}/runs/${this.runId}`, {
                    signal: this.abortController.signal
                });
                this.runStatus = response.data.status;
                if (response.data.status === 'completed') {
                    clearInterval(this.feedbackPollTimer);
                    if (!this.completedRunIds.includes(this.runId)) {
                        await this.getMessages();
                    }
                    this.completedRunIds.push(this.runId);
                }
            }
            catch (e) {
                if (this.logToConsole) {
                    if (e.name === 'AbortError') {
                        console.error('pollStatus was aborted');
                    }
                    else {
                        console.error(e);
                    }
                }
                if (e.name === 'AbortError') {
                    this.killFeedbackPollTimer();
                }
            }
        }, 1000);
    }
    killFeedbackPollTimer() {
        // console.log('killing feedback poll timer')
        clearInterval(this.feedbackPollTimer);
    }
    async pollScoringStatus() {
        clearInterval(this.scoringPollTimer);
        this.scoringPollTimer = setInterval(async () => {
            try {
                let response = await axios.get(`${this.baseUrl}/api/openai/thread/${this.threadId}/runs/${this.runId}`, {
                    signal: this.abortController.signal
                });
                if (response.data.status === 'requires_action') {
                    // console.log('polling score status')
                    const functions = response.data.required_action.submit_tool_outputs.tool_calls.filter((tool_call) => {
                        return tool_call.type === 'function';
                    });
                    this.handleFunctionCalls(functions);
                    this.killScoringPollTimer();
                }
                if (response.data.status === 'completed') {
                    this.killScoringPollTimer();
                    // console.log('score status complete')
                    if (!this.completedRunIds.includes(this.runId)) {
                        await this.getMessages();
                    }
                    this.completedRunIds.push(this.runId);
                }
            }
            catch (e) {
                if (this.logToConsole) {
                    if (e.name === 'AbortError') {
                        console.error('pollScoringStatus was aborted');
                    }
                    else {
                        console.error(e);
                    }
                }
                if (e.name === 'AbortError') {
                    this.killScoringPollTimer();
                }
            }
        }, 1000);
    }
    killScoringPollTimer() {
        // console.log('killing scoring poll timer')
        clearInterval(this.scoringPollTimer);
    }
    /**
     * LEGACY: Handle function calls from Assistants API
     * Used for scoring assistants with GPT-4 models
     */
    handleFunctionCalls(funcs) {
        // we only want to handle the function calls from
        // a scoring assistant once so just return if this
        // ends up getting called again in the event we get
        // more than one response with a requires_action status
        if (this.aborted || this.functionCallsHandled) {
            // console.log('aborting handleFunctionCalls')
            return;
        }
        if (this.logToConsole) {
            console.log('FUNCTION CALLS (Assistants API)');
            console.log(funcs);
        }
        let grade = {};
        funcs.forEach((func) => {
            let args = JSON.parse(func.function.arguments);
            Object.entries(args).forEach(([key, value]) => {
                grade[key] = value;
            });
        });
        this.grade = grade;
        let outputs = [];
        funcs.forEach((func) => {
            outputs.push({
                tool_call_id: func.id,
                output: 'ok'
            });
        });
        this.submitFunctionOutputs(outputs, this.grade);
        this.grade['assistantId'] = this.assistantId;
        // @ts-ignore
        if (this.getScoreCallback) {
            // @ts-ignore
            if (this.grade['meter']) {
                // normalize this value since OpenAI randomly capitalizes it sometimes
                this.grade['meter'] = this.grade['meter'].toLowerCase();
            }
            // @ts-ignore
            this.getScoreCallback(this.grade);
        }
        this.functionCallsHandled = true;
    }
    /**
     * MODERN: Handle tool calls from Responses API
     * Simpler than legacy - no need to submit outputs back
     */
    handleToolCalls(toolCalls) {
        if (this.aborted || this.functionCallsHandled) {
            return;
        }
        if (this.logToConsole) {
            console.log('=== HANDLE TOOL CALLS (Responses API) ===');
            console.log('Tool calls received:', JSON.stringify(toolCalls, null, 2));
        }
        let grade = {};
        toolCalls.forEach((call) => {
            if (this.logToConsole) {
                console.log('Processing call:', {
                    type: call.type,
                    name: call.name,
                    has_arguments: !!call.arguments
                });
            }
            // Responses API uses flat structure: call.type = 'function_call', call.name, call.arguments
            if (call.type === 'function_call' && call.name === 'evaluateMeter') {
                if (this.logToConsole) {
                    console.log('Match found! Parsing arguments:', call.arguments);
                }
                let args = JSON.parse(call.arguments || '{}');
                if (this.logToConsole) {
                    console.log('Parsed args:', args);
                }
                Object.entries(args).forEach(([key, value]) => {
                    grade[key] = value;
                });
            }
            else {
                if (this.logToConsole) {
                    console.log('No match - type:', call.type, 'name:', call.name);
                }
            }
        });
        if (this.logToConsole) {
            console.log('Final grade object:', grade);
        }
        this.grade = grade;
        this.grade['assistantId'] = this.assistantId;
        // @ts-ignore
        if (this.getScoreCallback) {
            // @ts-ignore
            if (this.grade['meter']) {
                this.grade['meter'] = this.grade['meter'].toLowerCase();
            }
            if (this.logToConsole) {
                console.log('Calling score callback with:', this.grade);
            }
            // @ts-ignore
            this.getScoreCallback(this.grade);
        }
        this.functionCallsHandled = true;
    }
    async submitFunctionOutputs(outputs = [], grade = {}) {
        const data = {
            outputs: {
                tool_outputs: outputs
            },
            grade: grade,
            signal: this.abortController.signal
        };
        try {
            // @ts-ignore
            axiosRetry(axios, {
                retries: 3, retryCondition: () => true, retryDelay: (retryCount) => {
                    return retryCount * 1000;
                }
            });
            await axios.post(`${this.baseUrl}/api/openai/thread/${this.threadId}/runs/${this.runId}`, data);
            await this.pollScoringStatus();
        }
        catch (e) {
            if (this.logToConsole) {
                if (e.name === 'AbortError') {
                    console.error('submitFunctionOutputs was aborted');
                }
                else {
                    console.error(e);
                }
            }
        }
    }
    async getMessages() {
        try {
            // All assistants now use local conversation history
            if (this.getMessagesCallback && this.conversationHistory.length > 0) {
                const messages = this.conversationHistory.map(msg => {
                    try {
                        // Only extract JSON from assistant messages
                        // User messages are always plain text
                        const text = msg.role === 'assistant'
                            ? this.extractTextFromResponse(msg.content)
                            : msg.content;
                        return {
                            role: msg.role,
                            text: text
                        };
                    }
                    catch (error) {
                        // If extraction fails, return raw content
                        if (this.logToConsole) {
                            console.error('Error extracting text in getMessages:', error, 'Content preview:', msg.content?.substring(0, 100));
                        }
                        return {
                            role: msg.role,
                            text: msg.content // Fallback to raw content
                        };
                    }
                });
                this.getMessagesCallback(this, messages);
                // Set feedback to last assistant message
                const lastAssistantMsg = this.conversationHistory
                    .slice()
                    .reverse()
                    .find(m => m.role === 'assistant');
                if (lastAssistantMsg) {
                    try {
                        this.feedback = this.extractTextFromResponse(lastAssistantMsg.content);
                    }
                    catch (error) {
                        if (this.logToConsole) {
                            console.error('Error extracting feedback from last message:', error);
                        }
                        this.feedback = lastAssistantMsg.content; // Fallback to raw content
                    }
                }
            }
        }
        catch (e) {
            if (this.logToConsole) {
                console.error('Error in getMessages:', e);
            }
        }
    }
    /**
     * Streaming for all models using Responses API
     * Backend normalizes SSE format for consistent handling
     */
    async startStreaming(callbacks = {}, forceFunctionCalls = false, toolOutputs = null, runId = null) {
        // All models now use Responses API endpoint
        const lastMessage = this.conversationHistory[this.conversationHistory.length - 1];
        if (!lastMessage) {
            if (this.logToConsole) {
                console.error('No message to stream');
            }
            return;
        }
        try {
            await this.instructionsRequest;
        }
        catch (error) {
            if (callbacks.onError) {
                callbacks.onError(error);
            }
            return;
        }
        // Reset function call handling for this stream
        this.functionCallsHandled = false;
        // Backend always loads messages from thread (no URL length limits)
        const paramData = {
            assistant_id: String(this.assistantId),
            thread_db_id: String(this.threadDbId),
            token: this.apiToken
        };
        const params = new URLSearchParams(paramData);
        const url = `${this.baseUrl}/api/openai/stream?${params.toString()}`;
        // Start SSE connection
        this.eventSource = new EventSource(url);
        let accumulatedText = '';
        // Handle SSE messages - IDENTICAL for both APIs now!
        this.eventSource.addEventListener('message', (event) => {
            try {
                if (event.data === '[DONE]') {
                    this.stopStreaming();
                    return;
                }
                const data = JSON.parse(event.data);
                // Handle text delta
                if (data.object === 'thread.message.delta' && data.delta) {
                    const textValue = data.delta.content?.[0]?.text?.value || '';
                    accumulatedText += textValue;
                    if (callbacks.onDelta) {
                        callbacks.onDelta(data.delta);
                    }
                }
                // Handle completion
                if (data.object === 'thread.message' && data.status === 'completed') {
                    const rawContent = data.content?.[0]?.text?.value || accumulatedText || '';
                    // Validate JSON if this is a json_schema response
                    // Don't call onComplete with malformed JSON
                    let isValidResponse = true;
                    let parsedContent = null;
                    if (this.outputFormat === 'json_schema') {
                        try {
                            const parsedResponse = JSON.parse(rawContent);
                            if (!parsedResponse || typeof parsedResponse !== 'object' || Array.isArray(parsedResponse)) {
                                throw new TypeError('JSON schema response must be an object');
                            }
                            parsedContent = parsedResponse;
                        }
                        catch (error) {
                            isValidResponse = false;
                            if (this.logToConsole) {
                                console.error('Malformed JSON in completion event, skipping:', error);
                                console.error('Raw content preview:', rawContent.substring(0, 500));
                            }
                            // Don't process this completion - likely incomplete/duplicate streaming data
                            return;
                        }
                    }
                    // Save to local conversation history
                    this.conversationHistory.push({
                        role: 'assistant',
                        content: rawContent
                    });
                    // Extract text using standard method (handles JSON if json_schema)
                    // Wrapped in try-catch to handle malformed JSON gracefully
                    try {
                        this.feedback = this.extractTextFromResponse(rawContent);
                    }
                    catch (error) {
                        if (this.logToConsole) {
                            console.error('Error extracting feedback text:', error);
                        }
                        this.feedback = rawContent; // Use raw content as fallback
                    }
                    if (this.type === 'scoring_percentage'
                        && this.getScoreCallback
                        && !this.functionCallsHandled
                        && parsedContent !== null) {
                        this.grade = {
                            ...parsedContent,
                            assistantId: this.assistantId
                        };
                        this.getScoreCallback(this.grade);
                        this.functionCallsHandled = true;
                    }
                    if (callbacks.onComplete && isValidResponse) {
                        callbacks.onComplete(data.content, data.response);
                    }
                    // Call messages callback
                    if (this.getMessagesCallback) {
                        const messages = this.conversationHistory.map(msg => {
                            try {
                                // Only extract JSON from assistant messages
                                // User messages are always plain text
                                const text = msg.role === 'assistant'
                                    ? this.extractTextFromResponse(msg.content)
                                    : msg.content;
                                return {
                                    role: msg.role,
                                    text: text
                                };
                            }
                            catch (error) {
                                // If extraction fails, return raw content
                                if (this.logToConsole) {
                                    console.error('Error extracting text from message:', error);
                                }
                                return {
                                    role: msg.role,
                                    text: msg.content
                                };
                            }
                        });
                        this.getMessagesCallback(this, messages);
                    }
                    this.stopStreaming();
                }
                // Handle requires_action (scoring - legacy only)
                if (data.object === 'thread.run' && data.status === 'requires_action') {
                    let grade = data.required_action.submit_tool_outputs.tool_calls[0].function.arguments;
                    grade = JSON.parse(grade);
                    grade = grade.meter;
                    if (callbacks.onRequiresAction) {
                        callbacks.onRequiresAction({
                            assistant_id: this.assistantId,
                            grade: grade,
                            tool_call_id: data.required_action.submit_tool_outputs.tool_calls[0].id,
                            run_id: data.id
                        });
                    }
                    if (callbacks.onComplete) {
                        callbacks.onComplete(event.data, data.response);
                    }
                    this.stopStreaming();
                }
                // Handle tool calls (scoring - Responses API)
                if (data.object === 'response.tool_calls' && data.tool_calls) {
                    // Use callback if provided, otherwise handle internally
                    if (callbacks.onRequiresAction) {
                        // Let Vue component handle it (avoids duplication)
                        callbacks.onRequiresAction({
                            assistant_id: this.assistantId,
                            tool_calls: data.tool_calls
                        });
                    }
                    else {
                        // No callback - handle internally
                        this.handleToolCalls(data.tool_calls);
                    }
                    if (callbacks.onComplete) {
                        callbacks.onComplete(event.data, data.response);
                    }
                    this.stopStreaming();
                }
            }
            catch (error) {
                if (this.logToConsole) {
                    console.error('Error processing SSE event:', error);
                }
                if (callbacks.onError)
                    callbacks.onError(error);
                this.stopStreaming();
            }
        });
        this.eventSource.addEventListener('open', (event) => {
            if (callbacks.onOpen)
                callbacks.onOpen(event);
        });
        this.eventSource.addEventListener('error', (error) => {
            if (this.logToConsole) {
                console.error('SSE connection error:', error);
            }
            if (callbacks.onError)
                callbacks.onError(error);
            this.stopStreaming();
        });
        this.eventSource.addEventListener('format', (event) => {
            try {
                const formatData = JSON.parse(event.data);
                this.outputFormat = formatData.format;
            }
            catch (error) {
                if (this.logToConsole) {
                    console.error('Error parsing format event:', error);
                }
            }
        });
    }
    async runBranchEvaluation(model = 'gpt-4o', messages = [], pathKeys = []) {
        try {
            // @ts-ignore
            axiosRetry(axios, {
                retries: 3,
                retryCondition: () => true,
                retryDelay: (retryCount) => {
                    return retryCount * 1000;
                }
            });
            let resp = await axios.post(`${this.baseUrl}/api/openai/run-branch-evaluation`, {
                model: model,
                messages: messages,
                pathKeys: pathKeys
            });
            return {
                success: true,
                data: resp.data
            };
        }
        catch (e) {
            if (this.logToConsole) {
                console.log(e);
            }
        }
    }
    stopStreaming(data = null) {
        if (this.eventSource) {
            // console.log('Closing SSE connection')
            this.eventSource.close();
            this.eventSource = null;
        }
    }
}
export default Openai;
